import { useEffect, useMemo, useSyncExternalStore } from 'react';
import {
  Button,
  Caption1,
  Field,
  MessageBar,
  MessageBarBody,
  Select,
  Spinner,
  Text,
  Textarea,
  makeStyles,
  tokens,
} from '@fluentui/react-components';
import type { ObservabilityEvidence, ObservabilityStatus } from '../../api/observabilityClient';
import { useAuthArgs } from '../../hooks/useAuthArgs';
import { canAskObservability, createObservabilitySession, lookbackOptions } from './observabilitySession';

const useStyles = makeStyles({
  page: { display: 'flex', flexDirection: 'column', gap: tokens.spacingVerticalL, minWidth: 0 },
  header: { display: 'flex', flexDirection: 'column', gap: tokens.spacingVerticalS },
  controls: { display: 'flex', flexWrap: 'wrap', alignItems: 'end', gap: tokens.spacingHorizontalM },
  target: { minWidth: '220px', maxWidth: '100%' },
  conversation: { display: 'flex', flexDirection: 'column', gap: tokens.spacingVerticalM, minWidth: 0 },
  message: {
    padding: tokens.spacingVerticalM,
    borderRadius: tokens.borderRadiusMedium,
    backgroundColor: tokens.colorNeutralBackground2,
    minWidth: 0,
  },
  text: { whiteSpace: 'pre-wrap', overflowWrap: 'anywhere', marginTop: tokens.spacingVerticalS },
  form: { display: 'flex', flexDirection: 'column', gap: tokens.spacingVerticalM },
  evidence: { marginTop: tokens.spacingVerticalM, minWidth: 0 },
  summary: { cursor: 'pointer', overflowWrap: 'anywhere', fontWeight: tokens.fontWeightSemibold },
  query: {
    whiteSpace: 'pre-wrap', overflowWrap: 'anywhere', padding: tokens.spacingVerticalM,
    backgroundColor: tokens.colorNeutralBackground3,
  },
  tableRegion: { overflowX: 'auto', maxHeight: '320px', marginTop: tokens.spacingVerticalS },
  table: {
    borderCollapse: 'collapse', width: '100%', textAlign: 'left',
    '& th, & td': {
      border: `1px solid ${tokens.colorNeutralStroke2}`,
      padding: tokens.spacingVerticalS, verticalAlign: 'top',
      whiteSpace: 'pre-wrap', overflowWrap: 'anywhere', minWidth: '100px',
    },
  },
});

export function ObservabilityPage() {
  const styles = useStyles();
  const getAuthArgs = useAuthArgs();
  const session = useMemo(() => createObservabilitySession(getAuthArgs), [getAuthArgs]);
  const state = useSyncExternalStore(session.subscribe, session.getSnapshot);
  const { status, loading, pending, result } = state;

  useEffect(() => {
    void session.load();
    return () => session.dispose();
  }, [session]);

  const ready = !!status?.enabled && status.configured && status.targets.length > 0;
  const icmReady = !!status?.icmEnabled && !!status.targets.find((target) => target.id === state.targetId)?.icmConfigured;

  return (
    <div className={styles.page}>
      <div className={styles.header}>
        <Text as="h1" size={700} weight="semibold">Observability Agent</Text>
        <Text>Ask read-only questions about operational telemetry and, when configured, IcM incidents for an approved target. Review the evidence before acting on an answer.</Text>
        <Caption1>Conversation history stays in this page’s memory and is cleared when you leave or change scope or chat model.</Caption1>
      </div>

      {loading && <Spinner label="Checking observability availability…" />}
      {state.error && (
        <MessageBar intent="error"><MessageBarBody>{state.error}</MessageBarBody></MessageBar>
      )}
      {state.notice && (
        <MessageBar intent="info"><MessageBarBody>{state.notice}</MessageBarBody></MessageBar>
      )}
      {status && !status.enabled && (
        <MessageBar intent="warning"><MessageBarBody>Observability is disabled in this environment.</MessageBarBody></MessageBar>
      )}
      {status?.enabled && !status.configured && (
        <MessageBar intent="warning"><MessageBarBody>Observability is not configured. Contact an administrator.</MessageBarBody></MessageBar>
      )}
      {status?.enabled && status.configured && status.targets.length === 0 && (
        <MessageBar intent="warning"><MessageBarBody>No observability targets are available. Contact an administrator.</MessageBarBody></MessageBar>
      )}
      {status?.enabled && status.configured && status.models.length === 0 && (
        <MessageBar intent="warning"><MessageBarBody>No eligible chat models are available. Use Fetch models in Model Endpoints, then Refresh availability.</MessageBarBody></MessageBar>
      )}
      {status?.enabled && status.models.length > 0 && !status.defaultModelId && !state.modelId && (
        <MessageBar intent="warning"><MessageBarBody>The default chat model is not available. Select a deployed chat model to continue.</MessageBarBody></MessageBar>
      )}

      <div className={styles.controls}>
        {ready && status && (
          <>
            <Field
              label="Chat model"
              className={styles.target}
              hint="Models come from the last Fetch models in Model Endpoints, not live health checks."
            >
              <Select value={state.modelId} disabled={status.models.length === 0} onChange={(_, data) => session.changeModel(data.value)}>
                {!state.modelId && <option value="" disabled>{status.models.length === 0 ? 'No chat models available' : 'Select a chat model'}</option>}
                {status.models.map((model) => <option key={model.id} value={model.id}>{model.displayName}</option>)}
              </Select>
            </Field>
            <Field label="Target" className={styles.target}>
              <Select value={state.targetId} onChange={(_, data) => session.changeScope(data.value, state.lookbackHours)}>
                {status.targets.map((target) => <option key={target.id} value={target.id}>{target.displayName}</option>)}
              </Select>
            </Field>
            <Field label="Lookback">
              <Select value={String(state.lookbackHours)} onChange={(_, data) => session.changeScope(state.targetId, Number(data.value))}>
                {lookbackOptions(status.maxLookbackHours).map((hours) => (
                  <option key={hours} value={hours}>{hours === 1 ? 'Last hour' : `Last ${hours} hours`}</option>
                ))}
              </Select>
            </Field>
          </>
        )}
        <Button disabled={loading || pending} onClick={() => void session.load()}>Refresh availability</Button>
        <Button disabled={loading || pending || !result} onClick={session.clear}>New conversation</Button>
      </div>

      {ready && status && (
        <>
          <IcmAvailability status={status} targetId={state.targetId} />
          <section className={styles.conversation} aria-label="Observability conversation" aria-live="polite">
            {state.history.slice(0, -2).map((message, index) => (
              <div key={index} className={styles.message}>
                <Text weight="semibold">{message.role === 'user' ? 'You' : 'Observability assistant'}</Text>
                <div className={styles.text}>{message.content}</div>
              </div>
            ))}
            {result && (
              <>
                <div className={styles.message}>
                  <Text weight="semibold">You</Text>
                  <div className={styles.text}>{result.question}</div>
                </div>
                <div className={styles.message}>
                  <Text weight="semibold">Observability assistant</Text>
                  <div className={styles.text}>{result.response.answer}</div>
                  {result.response.evidence.length === 0
                    ? <p>No evidence was returned for this answer.</p>
                    : result.response.evidence.map((evidence, index) => <Evidence key={index} evidence={evidence} />)}
                </div>
              </>
            )}
          </section>
          <form className={styles.form} onSubmit={(event) => { event.preventDefault(); void session.send(); }}>
            <Field
              label="Question"
              hint={`Ask about Azure Monitor telemetry${icmReady ? ' or IcM incidents' : ''}. ${state.question.length} / ${status.maxQuestionLength} characters. Context is limited to ${status.maxHistoryMessages} previous messages, each up to ${status.maxQuestionLength} characters. The current answer is shown in full.`}
              validationState={state.question.length > status.maxQuestionLength ? 'error' : 'none'}
              validationMessage={state.question.length > status.maxQuestionLength ? 'Shorten your question before sending.' : undefined}
            >
              <Textarea
                value={state.question}
                onChange={(_, data) => session.setQuestion(data.value)}
                maxLength={status.maxQuestionLength}
                rows={4}
                resize="vertical"
                disabled={pending}
                placeholder={icmReady ? 'Ask about errors, latency, or recent IcM incidents…' : 'Ask about errors, latency, or recent failures…'}
              />
            </Field>
            <div className={styles.controls}>
              <Button type="submit" appearance="primary" disabled={!canAskObservability(state)}>Send question</Button>
              {pending && (
                <>
                  <Button type="button" onClick={session.cancel}>Cancel</Button>
                  <Spinner size="tiny" label="Analyzing evidence…" />
                </>
              )}
            </div>
          </form>
        </>
      )}
    </div>
  );
}

export function IcmAvailability({ status, targetId }: { status: ObservabilityStatus; targetId: string }) {
  const target = status.targets.find((item) => item.id === targetId);
  if (!target) return null;
  const message = !status.icmEnabled
    ? `IcM incident evidence is ${status.icmEnabled === false ? 'disabled' : 'unavailable'} in this environment. Azure Monitor telemetry remains available.`
    : !target.icmConfigured
      ? 'IcM incident evidence is not configured for this target. Azure Monitor telemetry remains available. Contact an administrator to configure IcM.'
      : 'IcM incident evidence is configured for this target. This is configuration readiness only; live connectivity and authentication have not been verified.';
  return (
    <MessageBar intent="info">
      <MessageBarBody><strong>IcM for {target.displayName}: </strong>{message}</MessageBarBody>
    </MessageBar>
  );
}

export function Evidence({ evidence }: { evidence: ObservabilityEvidence }) {
  const styles = useStyles();
  const isIcm = evidence.source === 'icm';
  const columns = [...new Set(evidence.rows.flatMap((row) => Object.keys(row)))];
  return (
    <details className={styles.evidence}>
      <summary className={styles.summary}>
        {isIcm ? 'IcM' : 'Azure Monitor'}: {evidence.title} — {evidence.tool} ({evidence.rows.length} rows{evidence.truncated ? ', truncated' : ''})
      </summary>
      <Text as="h3" weight="semibold">{isIcm ? 'IcM request / scope' : 'Query (KQL)'}</Text>
      <pre className={styles.query}>{evidence.query || (isIcm ? 'No request scope was returned.' : 'No query text was returned.')}</pre>
      {evidence.notice && (
        <MessageBar intent="info"><MessageBarBody>{evidence.notice}</MessageBarBody></MessageBar>
      )}
      {evidence.truncated && (
        <MessageBar intent="warning"><MessageBarBody>Results are truncated; these rows may not show the full picture.</MessageBarBody></MessageBar>
      )}
      {evidence.rows.length === 0 ? <p>No rows were returned.</p> : (
        <div className={styles.tableRegion} role="region" aria-label={`${evidence.title} evidence rows`} tabIndex={0}>
          <table className={styles.table}>
            <thead><tr>{columns.map((column) => <th key={column} scope="col">{column}</th>)}</tr></thead>
            <tbody>
              {evidence.rows.map((row, index) => (
                <tr key={index}>{columns.map((column) => <td key={column}>{Object.hasOwn(row, column) ? row[column] ?? '—' : '—'}</td>)}</tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </details>
  );
}
